import React, { useEffect, useState } from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { normalizeMessagingUser } from './messagingUserUtils';
import { applyProfileAvatarImgFallback } from '../profilePictureUrl';

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function resolveSender(message, viewerUser, otherUser) {
  return message.sender
    ? normalizeMessagingUser(message.sender)
    : message.isMine
      ? normalizeMessagingUser(viewerUser)
      : normalizeMessagingUser(otherUser);
}

function resolveAvatar(message, viewerUser, otherUser) {
  const sender = resolveSender(message, viewerUser, otherUser);
  return sender?.profilePictureUrl || '';
}

function resolveSenderLabel(message, viewerUser, otherUser) {
  const sender = message.sender
    ? normalizeMessagingUser(message.sender)
    : message.isMine
      ? normalizeMessagingUser(viewerUser)
      : normalizeMessagingUser(otherUser);
  return sender?.displayName || (message.isMine ? 'You' : 'User');
}

export default function MessagingThreadMessage({
  message,
  viewerUser,
  otherUser,
  busy = false,
  onEdit,
  onDelete,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body || '');

  useEffect(() => {
    if (!editing) setDraft(message.body || '');
  }, [message.body, editing]);

  const sender = resolveSender(message, viewerUser, otherUser);
  const avatarSrc = resolveAvatar(message, viewerUser, otherUser);
  const senderLabel = resolveSenderLabel(message, viewerUser, otherUser);
  const canManage = message.isMine && !message.isDeleted && !editing;

  const saveEdit = async () => {
    const text = String(draft || '').trim();
    if (!text || text === String(message.body || '').trim()) {
      setEditing(false);
      return;
    }
    const ok = await onEdit?.(message.messageId, text);
    if (ok !== false) setEditing(false);
  };

  const cancelEdit = () => {
    setDraft(message.body || '');
    setEditing(false);
  };

  return (
    <div
      className={`messaging-thread__message-row${message.isMine ? ' is-mine' : ' is-theirs'}${
        message.isDeleted ? ' is-deleted' : ''
      }${editing ? ' is-editing' : ''}`}
    >
      {!message.isMine ? (
        <img
          src={avatarSrc}
          alt=""
          className="messaging-thread__message-avatar"
          title={senderLabel}
          onError={(e) => {
            applyProfileAvatarImgFallback(e.currentTarget, {
              userId: sender?.userId,
              name: sender?.displayName,
            });
          }}
        />
      ) : null}

      <div className={`messaging-thread__bubble-stack${editing ? ' is-editing' : ''}`}>
        {!editing ? (
          <span className="messaging-thread__bubble-sender">{senderLabel}</span>
        ) : null}
        <div
          className={`messaging-thread__bubble${message.isMine ? ' is-mine' : ' is-theirs'}${
            message.isDeleted ? ' is-deleted' : ''
          }${editing ? ' is-editing' : ''}`}
        >
          {message.isDeleted ? (
            <p className="messaging-thread__bubble-text messaging-thread__bubble-text--deleted">
              Message deleted
            </p>
          ) : editing ? (
            <div className="messaging-thread__edit">
              <label className="messaging-thread__edit-label" htmlFor={`edit-${message.messageId}`}>
                Edit message
              </label>
              <textarea
                id={`edit-${message.messageId}`}
                className="messaging-thread__edit-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={3}
                maxLength={4000}
                disabled={busy}
                aria-label="Edit message"
                autoFocus
              />
              <div className="messaging-thread__edit-actions">
                <button
                  type="button"
                  className="messaging-thread__action-chip noOutline-text"
                  onClick={cancelEdit}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="messaging-thread__action-chip messaging-thread__action-chip--primary noOutline-text"
                  onClick={saveEdit}
                  disabled={busy || !String(draft || '').trim()}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="messaging-thread__bubble-text">{message.body}</p>
              <time>
                {formatTime(message.createdAt)}
                {message.isEdited ? (
                  <span className="messaging-thread__edited"> · edited</span>
                ) : null}
              </time>
            </>
          )}
        </div>

        {canManage ? (
          <div className="messaging-thread__bubble-actions">
            <button
              type="button"
              className="messaging-thread__bubble-action noOutline-text"
              onClick={() => setEditing(true)}
              disabled={busy}
              aria-label="Edit message"
              title="Edit"
            >
              <FiEdit2 aria-hidden />
            </button>
            <button
              type="button"
              className="messaging-thread__bubble-action noOutline-text"
              onClick={() => onDelete?.(message.messageId)}
              disabled={busy}
              aria-label="Delete message"
              title="Delete"
            >
              <FiTrash2 aria-hidden />
            </button>
          </div>
        ) : null}
      </div>

      {message.isMine ? (
        <img
          src={avatarSrc}
          alt=""
          className="messaging-thread__message-avatar"
          title={senderLabel}
          onError={(e) => {
            applyProfileAvatarImgFallback(e.currentTarget, {
              userId: sender?.userId,
              name: sender?.displayName,
            });
          }}
        />
      ) : null}
    </div>
  );
}
